import { create } from 'zustand';
import { contentApi, fanApi, platformApi, dashboardApi } from '../services/api';
import type { PagedData, ListQueryRequest } from '../types';

// 文章统计
interface Article {
  id: string;
  title: string;
  author?: string;
  publishDate: string;
  articleUrl?: string;
  totalReadCount: number;
  totalReadUsers: number;
  shareCount: number;
  shareUsers: number;
  likeCount: number;
  viewCount: number;
  commentCount: number;
  favoriteCount: number;
  readCompletionRate: number;
  officialAccountReadCount?: number;
  deliveredUsers?: number;
  deliveredReadRate?: number;
  firstShareCount?: number;
  shareGeneratedReadCount?: number;
  category?: string;
  dataYear?: number;
  dataMonth?: number;
  createdAt?: string;
  wechatAccountName?: string;
}

// 菜单统计
interface MenuStat {
  id: string;
  menuName: string;
  parentMenu?: string;
  clickCount: number;
  clickUsers: number;
  statDate: string;
}

// 消息统计
interface MessageStat {
  id: string;
  msgType: string;
  receiveCount: number;
  sendCount: number;
  statDate: string;
}

// 央媒转发
interface CentralMediaRepost {
  id: string;
  articleTitle: string;
  mediaName: string;
  repostDate: string;
  readCount: number;
}

// 粉丝增长
interface FanGrowth {
  statDate: string;
  newFans: number;
  unfollowFans: number;
  netGrowth: number;
  totalFans: number;
}

// 用户活跃
interface UserActivity {
  statDate: string;
  activityType: string;
  activeUsers: number;
  newUsers: number;
  returnUsers: number;
}

// 消息关键词
interface MessageKeyword {
  id: string;
  statDate?: string;
  keyword?: string;
  occurCount: number;
}

// 账号每日阅读来源
interface AccountReadSource {
  id: string;
  statDate?: string;
  channel?: string;
  readCount: number;
}

// 每日分享统计
interface ShareStat {
  id: string;
  statDate?: string;
  shareUsers: number;
  readOriginalUsers: number;
  favoriteUsers: number;
  postCount: number;
}

// 平台统计
interface PlatformStat {
  id: string;
  statDate: string;
  dataYear: number;
  dataMonth: number;
  platformName: string;
  platformType?: string;
  fansCount: number;
  newFans: number;
  readCount: number;
  playCount: number;
  interactionCount: number;
  originalContentCount: number;
  shareCount: number;
  commentCount: number;
  likeCount: number;
}

// 文章扩展属性
interface ArticleExtension {
  id: string;
  articleId: string;
  articleTitle: string;
  articleType?: string;
  isOriginal: boolean;
  is100KPlus: boolean;
  isPinned: boolean;
  fromSubscription: number;
  fromFriends: number;
  fromHistory: number;
  fromSearch: number;
  fromOther: number;
  titleType?: string;
  coverType?: string;
  titleLength: number;
  hasEmoji: boolean;
  keywords?: string;
  author?: string;
  editor?: string;
  designer?: string;
  source?: string;
  sourceDeptCount: number;
  authorCount: number;
  photographerCount: number;
  designerCount: number;
  editorCount: number;
  wechatAccountName?: string;
  publishDate?: string;
  totalReadCount: number;
  positiveComments: number;
  neutralComments: number;
  negativeComments: number;
  questionComments: number;
  authorReplyRate: number;
  shareGeneratedReads: number;
  spreadLevel: number;
}

// 文章每日渠道统计
interface ArticleDailyStat {
  id: string;
  articleId: string;
  statDate: string;
  channel: string;
  readUsers: number;
  readCount: number;
  shareUsers: number;
  shareCount: number;
}

interface ContentState {
  // 文章统计
  articles: PagedData<Article> | null;
  articleSummary: {
    totalArticles: number;
    totalReads: number;
    totalShares: number;
    avgCompletionRate: number;
  } | null;
  
  // 菜单统计
  menuStats: PagedData<MenuStat> | null;
  
  // 消息统计
  messageStats: PagedData<MessageStat> | null;
  messageDistribution: any[];
  
  // 央媒转发
  centralMediaReposts: PagedData<CentralMediaRepost> | null;
  
  // 粉丝增长
  fanGrowth: PagedData<FanGrowth> | null;
  fanGrowthStats: {
    totalFans: number;
    newFans: number;
    unfollowFans: number;
    netGrowth: number;
  } | null;
  
  // 用户活跃
  userActivity: PagedData<UserActivity> | null;
  readSourceStats: any[];

  // 消息关键词
  messageKeywords: PagedData<MessageKeyword> | null;

  // 账号每日阅读来源
  accountReadSources: PagedData<AccountReadSource> | null;

  // 每日分享统计
  shareStats: PagedData<ShareStat> | null;

  // 平台统计
  platformStats: PagedData<PlatformStat> | null;

  // 文章扩展属性
  articleExtensions: PagedData<ArticleExtension> | null;

  // 文章每日渠道统计
  articleDailyStats: PagedData<ArticleDailyStat> | null;

  // 文章用户画像
  articleDemographics: PagedData<any> | null;
  
  // Dashboard
  dashboardKPIs: {
    totalReads: number;
    totalFans: number;
    totalArticles: number;
    growthRate: number;
  } | null;
  hotArticles: any[];
  readTrend: any[];
  
  // 加载状态
  isLoading: boolean;
  error: string | null;
}

interface ContentActions {
  // 文章统计
  fetchArticles: (params: ListQueryRequest & { startDate?: string; endDate?: string; category?: string; wechatAccountId?: string; sortBy?: string; descending?: boolean }) => Promise<void>;
  fetchArticleSummary: (params?: { startDate?: string; endDate?: string; wechatAccountId?: string; keyword?: string; category?: string }) => Promise<void>;
  
  // 菜单统计
  fetchMenuStats: (params: ListQueryRequest & { startDate?: string; endDate?: string }) => Promise<void>;
  
  // 消息统计
  fetchMessageStats: (params: ListQueryRequest & { startDate?: string; endDate?: string }) => Promise<void>;
  fetchMessageDistribution: () => Promise<void>;
  
  // 央媒转发
  fetchCentralMediaReposts: (params: ListQueryRequest) => Promise<void>;
  
  // 粉丝增长
  fetchFanGrowth: (params?: ListQueryRequest & { startDate?: string; endDate?: string }) => Promise<void>;
  fetchFanGrowthStats: () => Promise<void>;
  
  // 用户活跃
  fetchUserActivity: (params?: ListQueryRequest & { startDate?: string; endDate?: string }) => Promise<void>;
  fetchReadSourceStats: () => Promise<void>;

  // 消息关键词
  fetchMessageKeywords: (params?: ListQueryRequest & { keyword?: string; startDate?: string; endDate?: string }) => Promise<void>;

  // 账号每日阅读来源
  fetchAccountReadSources: (params?: ListQueryRequest & { startDate?: string; endDate?: string; channel?: string }) => Promise<void>;

  // 每日分享统计
  fetchShareStats: (params?: ListQueryRequest & { startDate?: string; endDate?: string }) => Promise<void>;

  // 平台统计
  fetchPlatformStats: (params?: ListQueryRequest & { startDate?: string; endDate?: string; platformName?: string }) => Promise<void>;

  // 文章扩展属性
  fetchArticleExtensions: (params?: ListQueryRequest & { isOriginal?: boolean }) => Promise<void>;

  // 文章每日渠道统计
  fetchArticleDailyStats: (params?: ListQueryRequest & { articleId?: string; startDate?: string; endDate?: string; channel?: string }) => Promise<void>;

  // 文章用户画像
  fetchArticleUserDemographics: (params?: ListQueryRequest & { articleId?: string; demographicType?: string }) => Promise<void>;
  
  // Dashboard
  fetchDashboardKPIs: () => Promise<void>;
  fetchHotArticles: (limit?: number) => Promise<void>;
  fetchReadTrend: (days?: number) => Promise<void>;
  fetchDashboardOverview: () => Promise<void>;
}

const useContentStore = create<ContentState & ContentActions>((set) => ({
  // 初始状态
  articles: null,
  articleSummary: null,
  
  menuStats: null,
  
  messageStats: null,
  messageDistribution: [],
  
  centralMediaReposts: null,
  
  fanGrowth: null,
  fanGrowthStats: null,
  
  userActivity: null,
  readSourceStats: [],

  // 消息关键词
  messageKeywords: null,

  // 账号每日阅读来源
  accountReadSources: null,

  // 每日分享统计
  shareStats: null,

  // 平台统计
  platformStats: null,

  // 文章扩展属性
  articleExtensions: null,

  // 文章每日渠道统计
  articleDailyStats: null,

  // 文章用户画像
  articleDemographics: null,
  
  dashboardKPIs: null,
  hotArticles: [],
  readTrend: [],
  
  isLoading: false,
  error: null,

  // 文章统计
  fetchArticles: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const result = await contentApi.getArticles(params);
      set({ 
        articles: result,
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message || '加载文章失败', isLoading: false });
      throw error;
    }
  },

  fetchArticleSummary: async (params = {}) => {
    try {
      const result = await contentApi.getArticleStats(params);
      set({
        articleSummary: {
          totalArticles: result.totalArticles || 0,
          totalReads: result.totalReadCount || 0,
          totalShares: result.totalShareCount || 0,
          avgCompletionRate: result.avgReadCompletionRate || 0,
        }
      });
    } catch (error: any) {
      console.error('加载文章统计失败:', error);
    }
  },

  // 菜单统计
  fetchMenuStats: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const result = await contentApi.getMenuStats(params);
      set({ 
        menuStats: result,
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message || '加载菜单统计失败', isLoading: false });
      throw error;
    }
  },

  // 消息统计
  fetchMessageStats: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const result = await contentApi.getMessageStats(params);
      set({ 
        messageStats: result,
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message || '加载消息统计失败', isLoading: false });
      throw error;
    }
  },

  fetchMessageDistribution: async () => {
    try {
      const result = await contentApi.getMessageTypeDistribution();
      set({ messageDistribution: result || [] });
    } catch (error: any) {
      console.error('加载消息分布失败:', error);
    }
  },

  // 央媒转发
  fetchCentralMediaReposts: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const result = await contentApi.getCentralMediaReposts(params);
      set({ 
        centralMediaReposts: result,
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message || '加载央媒转发失败', isLoading: false });
      throw error;
    }
  },

  // 粉丝增长
  fetchFanGrowth: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const result = await fanApi.getFanGrowth(params);
      set({ 
        fanGrowth: result,
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message || '加载粉丝增长失败', isLoading: false });
      throw error;
    }
  },

  fetchFanGrowthStats: async () => {
    try {
      const result = await fanApi.getFanGrowthStats();
      set({ fanGrowthStats: result });
    } catch (error: any) {
      console.error('加载粉丝统计失败:', error);
    }
  },

  // 用户活跃
  fetchUserActivity: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const result = await fanApi.getUserActivity(params);
      set({ 
        userActivity: result,
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message || '加载用户活跃失败', isLoading: false });
      throw error;
    }
  },

  fetchReadSourceStats: async () => {
    try {
      const result = await fanApi.getReadSourceStats();
      set({ readSourceStats: result || [] });
    } catch (error: any) {
      console.error('加载阅读来源失败:', error);
    }
  },

  // 消息关键词
  fetchMessageKeywords: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const result = await contentApi.getMessageKeywords(params);
      set({
        messageKeywords: result,
        isLoading: false
      });
    } catch (error: any) {
      set({ error: error.message || '加载消息关键词失败', isLoading: false });
      throw error;
    }
  },

  // 账号每日阅读来源
  fetchAccountReadSources: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const result = await contentApi.getAccountReadSources(params);
      set({
        accountReadSources: result,
        isLoading: false
      });
    } catch (error: any) {
      set({ error: error.message || '加载阅读来源数据失败', isLoading: false });
      throw error;
    }
  },

  // 每日分享统计
  fetchShareStats: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const result = await contentApi.getShareStats(params);
      set({
        shareStats: result,
        isLoading: false
      });
    } catch (error: any) {
      set({ error: error.message || '加载分享统计数据失败', isLoading: false });
      throw error;
    }
  },

  // 平台统计
  fetchPlatformStats: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const result = await platformApi.getPlatformStats(params);
      set({
        platformStats: result,
        isLoading: false
      });
    } catch (error: any) {
      set({ error: error.message || '加载平台统计数据失败', isLoading: false });
      throw error;
    }
  },

  // 文章扩展属性
  fetchArticleExtensions: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const result = await contentApi.getArticleExtensions(params);
      set({
        articleExtensions: result,
        isLoading: false
      });
    } catch (error: any) {
      set({ error: error.message || '加载文章扩展属性失败', isLoading: false });
      throw error;
    }
  },

  // 文章每日渠道统计
  fetchArticleDailyStats: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const result = await contentApi.getArticleDailyStats(params);
      set({
        articleDailyStats: result,
        isLoading: false
      });
    } catch (error: any) {
      set({ error: error.message || '加载文章每日统计数据失败', isLoading: false });
      throw error;
    }
  },

  // 文章用户画像
  fetchArticleUserDemographics: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const result = await contentApi.getArticleUserDemographics(params);
      set({
        articleDemographics: result,
        isLoading: false
      });
    } catch (error: any) {
      set({ error: error.message || '加载文章用户画像失败', isLoading: false });
      throw error;
    }
  },

  // Dashboard
  fetchDashboardKPIs: async () => {
    try {
      const result = await dashboardApi.getKPIs();
      set({ dashboardKPIs: result });
    } catch (error: any) {
      console.error('加载KPI失败:', error);
    }
  },

  fetchHotArticles: async (limit = 10) => {
    try {
      const result = await dashboardApi.getHotArticles(limit);
      set({ hotArticles: result || [] });
    } catch (error: any) {
      console.error('加载热门文章失败:', error);
    }
  },

  fetchReadTrend: async (days = 30) => {
    try {
      const result = await dashboardApi.getReadTrend(days);
      set({ readTrend: result || [] });
    } catch (error: any) {
      console.error('加载阅读趋势失败:', error);
    }
  },

  fetchDashboardOverview: async () => {
    try {
      const result = await dashboardApi.getOverview();
      // 兼容不同的响应格式
      if (result) {
        set({ 
          dashboardKPIs: result.kpis || result,
          hotArticles: result.hotArticles || []
        });
      }
    } catch (error: any) {
      console.error('加载概览失败:', error);
    }
  },
}));

export { useContentStore };
export type { Article, MenuStat, MessageStat, CentralMediaRepost, FanGrowth, UserActivity, ArticleDailyStat, ArticleExtension, PlatformStat, MessageKeyword, AccountReadSource, ShareStat };
